import { atom } from 'jotai'
import type { JobApplicationStageDefinition } from '../services/api'

// JobOpportunity.status is a free-form string that must match the stageId of
// some active JobApplicationStageDefinition row in DynamoDB. The seven values
// below match the system-seeded defaults (the client's own Momentum Maximizer
// Status dropdown); once the configurable kanban is wired up, admin-created
// stages can extend this set.
export type JobOpportunityStatus = string

// Fallback labels and colors used only when the stage-definitions atom hasn't
// hydrated yet (e.g. first paint before the GET /job-application-stages call
// returns) or in a defensive renderer path. Live data should always prefer
// the atom-driven values.
// Adopted verbatim from the client's own Momentum Maximizer Status dropdown.
// 'got_the_job' replaces the retired 'offer'; 'closed_not_accepting' replaces
// the retired 'closed'; 'following_up' and 'rejected' are new.
//
// 'applying' is deliberately absent: awaiting the client's decision is the
// absence of a status, not a status of its own. Its label and colour are kept
// below so a record that still holds the retired value renders a name rather
// than a raw slug until the migration runs.
export const FALLBACK_JOB_STATUSES = [
  'applied',
  'following_up',
  'interviewing',
  'got_the_job',
  'rejected',
  'closed_not_accepting',
] as const
export const FALLBACK_JOB_STATUS_LABELS: Record<string, string> = {
  // Retired, kept for records that still hold it — see FALLBACK_JOB_STATUSES.
  applying: 'Applying',
  applied: 'Applied',
  following_up: 'Following up',
  interviewing: 'Interviewing',
  got_the_job: 'Got the Job',
  rejected: 'Rejected',
  closed_not_accepting: 'Closed/Not Accepting Anymore',
}
export const FALLBACK_JOB_STATUS_COLORS: Record<string, string> = {
  applying: '#4db6ac',
  applied: '#42a5f5',
  following_up: '#ab47bc',
  interviewing: '#ff9800',
  got_the_job: '#66bb6a',
  rejected: '#e53935',
  closed_not_accepting: '#9e9e9e',
}

// Back-compat re-exports — some files still import these by the old name.
// New code should use FALLBACK_JOB_* or the live atom values.
export const JOB_STATUSES = FALLBACK_JOB_STATUSES
export const JOB_STATUS_LABELS = FALLBACK_JOB_STATUS_LABELS
export const JOB_STATUS_COLORS = FALLBACK_JOB_STATUS_COLORS

// Transitional: a record may still hold a legacy status ('offer', 'closed')
// retired by the status-vocabulary migration, until that migration is applied.
// JOB_STATUS_LABELS has no entries for them, so a naive `JOB_STATUS_LABELS[x]
// || x` display renders the raw token ("offer") instead of a real label.
// Route every read-only status display through this resolver instead of
// patching each call site, so the next one added can't miss it. Remove once
// the migration ships and no live record can hold these values.
const LEGACY_JOB_STATUS_LABELS: Record<string, string> = {
  offer: FALLBACK_JOB_STATUS_LABELS.got_the_job,
  closed: FALLBACK_JOB_STATUS_LABELS.closed_not_accepting,
}

export function resolveJobStatusLabel(status: string): string {
  return JOB_STATUS_LABELS[status] || LEGACY_JOB_STATUS_LABELS[status] || status
}

// 'rejected' and 'position_filled' moved to JOB_STATUSES ('rejected' and
// 'closed_not_accepting') — the backend's CLOSE_REASONS no longer accepts
// them, so they're dropped here too rather than offering values it would 400.
export const CLOSE_REASONS = [
  { value: 'withdrew', label: 'Withdrew' },
  { value: 'no_response', label: 'No Response' },
  { value: 'other', label: 'Other' },
] as const

// How much a company matters is chosen once, on the company, in the client's
// Dream Job target-companies list — never per-application. An application's
// tier is derived server-side from that list and shown here read-only.
// Four tiers are the client's own ranking of a company they listed. 'unlisted'
// is a company that is not on the list at all — derived, never chosen — so it
// is a filter and a chip but not an option in the Dream Job form. Both
// 'wildcard' and 'unlisted' are real tiers, not missing values, and most
// applications land in one of them, so their chips must read as deliberate and
// calm rather than a warning. Every tier is distinguishable by its label text
// as well as its color; never color alone.
export const COMPANY_TIERS = [
  { value: 'dream', label: 'Dream' },
  { value: 'target', label: 'Target' },
  { value: 'backup', label: 'Back-Up' },
  { value: 'wildcard', label: 'Wildcard' },
  { value: 'unlisted', label: 'Unlisted' },
] as const

export type CompanyTier = (typeof COMPANY_TIERS)[number]['value']

export const COMPANY_TIER_LABELS: Record<string, string> = Object.fromEntries(
  COMPANY_TIERS.map((t) => [t.value, t.label])
)

// MUI Chip `color` prop — resolved from the theme palette at render time, so
// no hex literals live here.
export const COMPANY_TIER_CHIP_COLOR: Record<string, 'primary' | 'success' | 'warning' | 'default'> = {
  dream: 'primary',
  target: 'success',
  backup: 'warning',
  wildcard: 'default',
  unlisted: 'default',
}

// A record may have no companyTier at all — e.g. while the backend that
// derives and populates it isn't deployed yet, or any other gap between a
// job existing and the server placing it on a tier. COMPANY_TIER_LABELS and
// COMPANY_TIER_CHIP_COLOR are plain maps, so an unresolved key yields
// undefined (a blank chip) rather than a value. Route every read of a job's
// tier through this resolver — for display and for filtering alike — so the
// fallback lives in one place and agrees with what the server itself derives
// for anything it can't place: 'unlisted'. A retired 'goal' (an older cached
// record or broadcast) is read as 'target', as the server reads it.
export function resolveCompanyTier(tier: CompanyTier | string | undefined | null): CompanyTier {
  if (tier === 'goal') return 'target'
  return (tier as CompanyTier) || 'unlisted'
}

// MWA-220 — the client's decision on a staff-sourced application, adopted
// verbatim from the client's own DECISION dropdown. `decisionRequested` is set
// at staff-create time; until the client picks one of these, the card is
// "awaiting decision". `not_interested` archives the card (kept, not deleted).
// `self_apply` keeps its value; its label reads as the client's own wording.
export const APPLY_DECISIONS = [
  { value: 'apply_on_behalf', label: 'Apply on my behalf' },
  { value: 'self_apply', label: 'I will apply' },
  { value: 'not_interested', label: 'Not Interested' },
  { value: 'not_suitable', label: 'Not Suitable' },
] as const

export type ApplyDecision = (typeof APPLY_DECISIONS)[number]['value']

export const APPLY_DECISION_LABELS: Record<string, string> = {
  apply_on_behalf: 'Apply on my behalf',
  self_apply: 'I will apply',
  not_interested: 'Not Interested',
  not_suitable: 'Not Suitable',
}

export const APPLY_DECISION_COLORS: Record<string, string> = {
  apply_on_behalf: '#42a5f5', // blue — needs staff action
  self_apply: '#66bb6a', // green — client is handling it
  not_interested: '#9e9e9e', // grey — declined/archived
  not_suitable: '#8d6e63', // brown — declined for fit, distinct from "not interested"
}

// MWA-304 — the kind of public event behind a hidden opportunity. Mirrors
// SIGNAL_TYPES in the backend's jobOpportunityRules.mjs, which is what
// actually rejects a bad value; see jobOpportunities.test.ts for the guard
// against the two drifting apart.
export const SIGNAL_TYPES = ['leadership_change', 'layoff', 'funding', 'restructure', 'other'] as const

export type SignalType = (typeof SIGNAL_TYPES)[number]

export const SIGNAL_TYPE_LABELS: Record<string, string> = {
  leadership_change: 'Leadership change',
  layoff: 'Layoff',
  funding: 'Funding',
  restructure: 'Restructure',
  other: 'Other',
}

// MWA-355 — the client's answer on a hidden opportunity. Mirrors
// CLIENT_PURSUITS in the backend's jobOpportunityRules.mjs, which is what
// actually rejects a bad value; jobOpportunities.test.ts guards the pair
// against drifting apart, the same way it does for SIGNAL_TYPES above.
//
// There is no 'awaiting' member. An unanswered opportunity carries no value at
// all, so every opportunity logged before this field existed reads as
// unanswered rather than as a fourth state nobody wrote.
export const CLIENT_PURSUITS = ['pursuing', 'declined'] as const

export type ClientPursuit = (typeof CLIENT_PURSUITS)[number]

// Written from the client's side of the conversation on both surfaces. Staff
// read these labels too, and "I'm going after this" says whose answer it is in
// a way that "Pursuing" does not.
export const CLIENT_PURSUIT_LABELS: Record<string, string> = {
  pursuing: 'Going after this',
  declined: 'Not pursuing',
}

export const CLIENT_PURSUIT_COLORS: Record<string, string> = {
  pursuing: '#66bb6a', // green — matches self_apply, the other "client is on it" answer
  declined: '#9e9e9e', // grey — matches not_interested, the other client decline
}

// MWA-355 — may the client still answer this one? Mirrors canAnswerPursuit in
// the backend jobOpportunityRules.mjs, which refuses the write; this copy
// decides whether to offer the controls at all. The two must agree, or the
// portal shows buttons whose request comes back 409.
//
// A status means staff converted it and the ordinary decision flow owns it
// from there; archived means it left the board. An already-answered
// opportunity is deliberately still answerable — changing a mistaken answer is
// the same write, and there is no other route back.
export function canAnswerPursuit(
  job: Pick<JobOpportunity, 'status' | 'hiddenOpportunity' | 'archived'> | null | undefined
): boolean {
  const j = job || ({} as JobOpportunity)
  if (j.hiddenOpportunity !== true) return false
  if (j.archived === true) return false
  return !j.status
}

// MWA-421 — which Signals sub-tab a hidden opportunity sits in. Mirrors
// WATCH_TYPES in the backend's jobOpportunityRules.mjs, which rejects a bad
// value; jobOpportunities.test.ts guards the pair against drifting.
export const WATCH_TYPES = ['signal', 'seat'] as const

export type WatchType = (typeof WATCH_TYPES)[number]

export const WATCH_TYPE_LABELS: Record<WatchType, string> = {
  signal: 'Signal Watch',
  seat: 'Seat Watch',
}

// Every record logged before the field existed was a seat with a person who
// left it, so an absent value reads as 'seat' — the same default the backend
// stores and the backfill writes. It is what keeps those rows visible before a
// backfill has run, rather than leaving them in neither table.
export const DEFAULT_WATCH_TYPE: WatchType = 'seat'

export function resolveWatchType(job: Pick<JobOpportunity, 'watchType'> | null | undefined): WatchType {
  const t = job?.watchType
  return t === 'signal' || t === 'seat' ? t : DEFAULT_WATCH_TYPE
}

// MWA-421 — the Action column. Mirrors SIGNAL_ACTIONS in the backend, in the
// same order; the values are what is stored, the labels are what the select
// shows. Order is the order the select lists them in.
export const SIGNAL_ACTIONS = ['pursue', 'not_interested', 'reaching_out', 'following_up', 'pending'] as const

export type SignalAction = (typeof SIGNAL_ACTIONS)[number]

export const SIGNAL_ACTION_LABELS: Record<SignalAction, string> = {
  pursue: 'Pursue',
  not_interested: 'Not Interested',
  reaching_out: 'Reaching Out',
  following_up: 'Following Up',
  pending: 'Pending',
}

// Mirrors effectiveSignalAction in the backend. An explicit signalAction wins;
// a record that only carries the MWA-355 answer reads through it, so a client
// who already answered is not reset to Pending by the column arriving.
export function effectiveSignalAction(
  job: Pick<JobOpportunity, 'signalAction' | 'clientPursuit'> | null | undefined
): SignalAction {
  const a = job?.signalAction
  if (a && (SIGNAL_ACTIONS as readonly string[]).includes(a)) return a as SignalAction
  if (job?.clientPursuit === 'pursuing') return 'pursue'
  if (job?.clientPursuit === 'declined') return 'not_interested'
  return 'pending'
}

// MWA-304 follow-up — mirrors isAwaitingClientDecision in the backend
// jobOpportunityRules.mjs. `!status` used to mean "waiting on the client";
// a hidden opportunity also has no status but is never put to the client,
// so the gates keyed on the raw check blocked every route out of the tab.
// The two copies must agree: this one blocks the save before the request,
// the backend refuses it after.
export function isAwaitingClientDecision(job: Pick<JobOpportunity, 'status' | 'hiddenOpportunity'> | null | undefined): boolean {
  const j = job || ({} as JobOpportunity)
  if (j.hiddenOpportunity === true) return false
  return !j.status || j.status === 'applying'
}

export const INACTIVITY_THRESHOLD_DAYS = 14

// MWA-250 — fit-score (1-5) badge color, banded to the prompt rubric: 5 is a
// bullseye, 4 a strong fit, 3 balanced, 1-2 weak or poor.
// A green→amber→red ramp so the score reads as a "match" signal, visually
// distinct from the category and decision chips (and the future employer-quality
// indicator on Target Companies).
export function fitScoreColor(score: number | null | undefined): string {
  if (score === null || score === undefined || Number.isNaN(Number(score))) return '#9e9e9e'
  const s = Number(score)
  if (s >= 5) return '#2e7d32' // green — bullseye
  if (s >= 4) return '#689f38' // light green — strong fit
  if (s >= 3) return '#f9a825' // amber — balanced
  return '#e53935' // red — weak or poor
}

export interface JobOpportunity {
  id: string
  clientId: string
  clientName: string
  company: string
  jobTitle: string
  status: JobOpportunityStatus
  applicationDate?: string
  // The client's sheet also tracks when the posting went up and when someone last
  // followed up with the employer. Both optional; distinct from
  // lastFollowUpReminderAt, which is when our own follow-up digest last nudged.
  datePosted?: string
  lastFollowUpDate?: string
  // MWA-297 — contacts staff attached by hand, on top of the ones the employer
  // -name match finds. Absent on older records; treat as empty.
  linkedContactIds?: string[]
  companyTier: CompanyTier
  location?: string
  notes?: string
  url?: string
  lastActivityDate: string
  closeReason?: string
  // MWA-304 — a role staff believe is opening from a public signal, logged
  // before any posting exists. Set once at creation; converting to a real
  // application sets a status and leaves this in place as provenance.
  hiddenOpportunity?: boolean
  // The signal itself: what happened, when it was seen, where it was seen,
  // and who is leaving the seat. Present only on a hidden opportunity, and
  // kept after conversion as the application's provenance.
  // `string`, not SignalType, on purpose. This codebase carries a second
  // JobOpportunity declaration in services/api.ts and assigns freely between
  // the two, which only compiles while they stay structurally identical. A
  // narrower union here breaks that assignment. SignalType still types the
  // picker, where the value is being chosen rather than read back.
  signalType?: string
  signalText?: string
  signalObservedDate?: string
  signalSource?: string
  vacatedBy?: string
  // MWA-355 — the client's own answer on a hidden opportunity: will they chase
  // it or not, plus who answered and when. Absent means nobody has answered
  // yet, which is why there is no 'awaiting' value — see CLIENT_PURSUITS.
  // `string`, not ClientPursuit, for the same reason as signalType above.
  clientPursuit?: string
  clientPursuitAt?: string
  clientPursuitBy?: string
  // MWA-421 — the Signals tables. Which sub-tab (absent reads as 'seat'), the
  // company's LinkedIn page, whether "no previous holder" was the answer to who
  // held the role (distinct from the name being left blank), and the Action
  // taken on it (absent reads through clientPursuit, then as Pending).
  // `string`, not the unions, for the same reason as signalType above.
  watchType?: string
  companyLinkedInUrl?: string
  noPreviousHolder?: boolean
  signalAction?: string
  // MWA-220 — client decision flow
  decisionRequested?: boolean
  applyDecision?: ApplyDecision
  decisionHistory?: { decision: string; changedBy: string; at: string }[]
  archived?: boolean
  archivedAt?: string | null
  pendingClearedAt?: string | null
  pendingClearedBy?: string
  // MWA-224 — set-once staff confirmation the app was submitted; gates Applying → Applied.
  submissionConfirmed?: boolean
  submissionConfirmedAt?: string
  submissionConfirmedBy?: string
  // MWA-250 — job fit score (1-5) + rationale; set manually or via AI generation.
  fitScore?: number
  fitScoreReason?: string
  fitScoreSource?: 'manual' | 'ai'
  fitScoreGeneratedAt?: string
  linkedInterviewId?: string
  statusHistory?: { status: string; timestamp: string; changedBy: string }[]
  hasDuplicate?: boolean
  isDeleted?: boolean
  createdBy?: string
  createdAt: string
  updatedAt: string
}

// Atoms
export const jobOpportunitiesAtom = atom<JobOpportunity[]>([])
export const jobOpportunitiesLoadingAtom = atom(true)

// Configurable job application stage definitions (loaded at App mount).
export const jobApplicationStageDefinitionsAtom = atom<JobApplicationStageDefinition[]>([])
export const jobApplicationStagesLoadingAtom = atom(true)

// Derived atom: grouped by status string. Returns an open record (any string
// key) instead of the old fixed enum so the kanban can render whatever
// columns the admin has configured — including any user-added stages.
export const jobOpportunitiesByStatusAtom = atom((get) => {
  const jobs = get(jobOpportunitiesAtom)
  const byStatus: Record<string, JobOpportunity[]> = {}

  for (const job of Array.isArray(jobs) ? jobs : []) {
    if (!job?.status) continue
    // MWA-220 — archived ("Not interested") cards are excluded from the active
    // stage columns and rendered in a separate "Archived / Not Interested" section.
    if (job.archived) continue
    if (!byStatus[job.status]) byStatus[job.status] = []
    byStatus[job.status].push(job)
  }

  return byStatus
})

// MWA-220 — applications a staff member sourced that are still awaiting the
// client's decision. Drives the client portal "Awaiting your decision" surface +
// nav badge. Keyed off `decisionRequested` so historical cards (which never had
// it set) are NOT surfaced.
//
// MWA-421 — a hidden opportunity is never in this queue: nobody is asked to
// decide it, and it now has a Signals page of its own. Matches
// isPendingClientDecision in the backend's jobOpportunityRules.mjs.
export const awaitingDecisionJobsAtom = atom((get) => {
  const jobs = get(jobOpportunitiesAtom)
  return (Array.isArray(jobs) ? jobs : []).filter(
    (j) => j?.decisionRequested && !j.applyDecision && !j.archived && j.hiddenOpportunity !== true
  )
})
